import React from 'react';
import { gql } from '@reverbdotcom/commons/src/gql';
import I18n from 'i18n-js';
import {
  Core_Listing_SimilarOutletListingsQuery,
  Core_Listing_SimilarOutletListingsQueryVariables,
  SimilarListingsFieldsFragment,
} from '@reverbdotcom/commons/src/gql/graphql';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { inExperiment, useUser } from '@reverbdotcom/commons/src/user_hooks';
import { eligibleForOutlet } from '@reverbdotcom/commons/src/user_context_helpers';
import { getRootCategorySlug } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import experiments from '@reverbdotcom/commons/src/experiments';
import ListingsCollection from '@reverbdotcom/commons/src/components/listings_collection';

interface Props {
  listing: SimilarListingsFieldsFragment;
}

const COMPONENT_NAME = 'OutletRowItemPage';
const SIMILAR_OUTLET_LISTINGS_MIN_THRESHOLD = 5;

export function SimilarOutletListingsRow(props: Props) {
  const user = useUser();
  const { data, loading } = useQuery<Core_Listing_SimilarOutletListingsQuery, Core_Listing_SimilarOutletListingsQueryVariables>(
    SIMILAR_OUTLET_LISTINGS_ROW,
    {
      ssr: false,
      errorPolicy: 'all',
      variables: {
        listingId: props.listing.id,
      },
    },
  );

  let similarListings;
  let title;

  if (data?.similarListingsSearch.listings.length >= SIMILAR_OUTLET_LISTINGS_MIN_THRESHOLD) {
    similarListings = data?.similarListingsSearch.listings;
    title = I18n.t('discovery.itemPageSimilarOutletListings.heading');

  } else {
    similarListings = data?.similarListingsSearch.fallbackListings;

    const rootCategorySlug = getRootCategorySlug(props.listing);
    const rootCategoryName = I18n.t(`discovery.categories.${rootCategorySlug}`);
    title = I18n.t('discovery.itemPageSimilarOutletListings.fallbackHeading', { rootCategoryName });
  }

  const event: MParticleEvent = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
    listingsCount: similarListings?.length || 0,
    title: title,
  };

  const shouldRender = !loading && eligibleForOutlet(user) && inExperiment(user, experiments.ITEM_PAGE_OUTLET_BROWSE_ROW) && similarListings.length >= SIMILAR_OUTLET_LISTINGS_MIN_THRESHOLD;

  useViewTracking(event, shouldRender);

  if (!shouldRender) return null;

  return (
    <ListingsCollection
      title={title}
      listings={similarListings}
      componentName={COMPONENT_NAME}
    />
  );
}

const SIMILAR_OUTLET_LISTINGS_ROW = gql(`
  query Core_Listing_SimilarOutletListings(
    $listingId: String
  ) {
    similarListingsSearch(
      input: {
        listingId: $listingId
        outlet: true
        useFallback: true
        includeForLiveListings: true
        limit: 12
      }
    ) {
      listings {
        _id
        usOutlet
        ... ListingsCollection
      }

      fallbackListings {
        _id
        usOutlet
        ... ListingsCollection
      }
    }
  }
`);
